import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { FAQItem } from '../types';

const FAQ: React.FC = () => {
  const [openId, setOpenId] = useState<string | null>('1');

  const faqs: FAQItem[] = [
    {
      id: '1',
      question: 'How long does a typical API integration project take?',
      answer: 'Project timelines vary based on complexity. Simple integrations (2 platforms, basic data sync) typically take 2-3 weeks. Moderate complexity projects (multiple platforms, custom logic) take 4-6 weeks. Complex enterprise integrations (legacy systems, high security requirements) can take 8-12 weeks. We provide detailed timelines during the discovery phase.'
    },
    {
      id: '2',
      question: 'What security measures do you implement?',
      answer: 'We implement enterprise-grade security including OAuth 2.0 authentication, JWT token management, SSL/TLS encryption, API key rotation, rate limiting, and comprehensive audit logging. All integrations undergo security vulnerability assessments and penetration testing. We maintain compliance with SOC 2, GDPR, HIPAA, and other relevant standards.'
    },
    {
      id: '3',
      question: 'Can you integrate with legacy systems?',
      answer: 'Yes, we specialize in legacy system integration. We have extensive experience with SOAP APIs, XML-RPC, custom protocols, and database-level integrations. Our approach includes creating modern API wrappers around legacy systems, ensuring backward compatibility while enabling modern integration capabilities.'
    },
    {
      id: '4',
      question: 'What happens if an API changes or breaks?',
      answer: 'We implement comprehensive monitoring and alerting systems that detect API changes or failures immediately. Our integrations include intelligent retry mechanisms, fallback procedures, and detailed error logging. We provide 24/7 support and maintain all integrations with proactive monitoring to address issues before they impact your business.'
    },
    {
      id: '5',
      question: 'How do you handle data migration?',
      answer: 'Data migration is executed in phases with comprehensive validation at each step. We create detailed migration plans, perform test migrations, validate data integrity, and implement rollback procedures. Historical data is migrated during off-peak hours with zero-downtime deployment strategies. All migrations include data reconciliation reports.'
    },
    {
      id: '6',
      question: 'What is your pricing model?',
      answer: 'We offer flexible pricing based on project scope: fixed-price for well-defined integrations, time-and-materials for exploratory projects, and retainer agreements for ongoing integration development. All projects include discovery phase, development, testing, deployment, and 30 days of post-launch support. Contact us for a detailed quote.'
    }
  ];

  const toggleFAQ = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-text-secondary">
              Technical answers to common integration questions
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div 
                key={faq.id}
                className="bg-card rounded-lg border border-border overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-surface transition-colors duration-fast"
                  aria-expanded={openId === faq.id}
                >
                  <span className="text-lg font-semibold text-text-primary pr-4">
                    {faq.question}
                  </span>
                  <Icon 
                    name="ChevronDown" 
                    size={20} 
                    className={`text-primary flex-shrink-0 transition-transform duration-fast ${
                      openId === faq.id ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                {openId === faq.id && (
                  <div className="px-6 pb-4 animate-fade-in">
                    <p className="text-text-secondary leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;